/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useMemo } from 'react';
import { Skeleton, Grid } from '@mui/material';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useUpdateDealRowMutation } from '@/features/deals/dealsApi';
import {
	NEEDS_ANALYSIS,
	SCOPE_OF_ADVICE,
} from '@/components/forms/CreateNewBusinessForm/validationSchema';
import { NeedsAnalysisSelect } from '@/components/selects/NeedsAnalysisSelect';
import { useGetClassesOfRiskQuery } from '@/features/classesOfRisk/classesOfRiskApi';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';

export const NeedsAnalysisDropdownContainer = () => {
	const { dealRow } = useObjectContext();

	const clientType = dealRow?.clientType;
	const scopeOfAdviceArray = dealRow?.[SCOPE_OF_ADVICE] ?? [];

	const needsAnalysis = useMemo(
		() => dealRow?.[NEEDS_ANALYSIS] ?? [],
		[dealRow?.[NEEDS_ANALYSIS]],
	);

	const { username: email } = useSelector((state) => state.msalAccount.account);

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const countryCode = userDetails?.broker?.country ?? 'NZ';
	const brokerGroupIds = userDetails?.brokerGroupArray;

	const { data: classesOfRisk, ...classesOfRiskQuery } = useGetClassesOfRiskQuery(
		{ countryCode, clientType, brokerGroupIds },
		{ skip: userDetails.isLoading || userDetails.isUninitialized },
	);

	const needsAnalysisDropdowns = scopeOfAdviceArray
		.map((value) =>
			typeof value === 'string'
				? classesOfRisk?.find((c) => c?.classOfRisk === value)
				: classesOfRisk?.find((c) => c?.classOfRisk === value?.classOfRisk),
		)
		.filter(Boolean)
		.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

	const [updateDeal, { isLoading }] = useUpdateDealRowMutation();

	const handleAnalysisChange = async (key, value) => {
		const existingEntryIndex = needsAnalysis.findIndex((item) => item.key === key);

		let updatedNeedsAnalysis;

		if (existingEntryIndex >= 0) {
			updatedNeedsAnalysis = needsAnalysis.map((item, index) =>
				index === existingEntryIndex ? { key, value } : item,
			);
		} else {
			updatedNeedsAnalysis = [...needsAnalysis, { key, value }];
		}

		await updateDeal({
			dealId: dealRow?.dealId,
			docId: dealRow?.id,
			properties: { [NEEDS_ANALYSIS]: updatedNeedsAnalysis },
		})
			.unwrap()
			.catch((err) => console.log('Error updating', err));
	};

	return (
		<Grid container rowSpacing={1} columnSpacing={2}>
			{classesOfRiskQuery.isLoading || classesOfRiskQuery.isUninitialized
				? scopeOfAdviceArray.map((value, index) => (
						<Grid size={6} key={`skeleton-${index}`}>
							<Skeleton width='100%' />
						</Grid>
					))
				: scopeOfAdviceArray.includes('fullNeedsAnalysis')
					? (classesOfRisk ?? []).map((item, index) => (
							<Grid
								size={6}
								key={`needs-scope-selection-${item?.classOfRisk}-${index}`}
							>
								<NeedsAnalysisSelect
									classOfRisk={item?.classOfRisk}
									label={item.label}
									analysisValue={
										needsAnalysis.find((v) => v.key == item?.classOfRisk)?.value
									}
									disabled={isLoading}
									setAnalysisValue={(val) =>
										handleAnalysisChange(item?.classOfRisk, val)
									}
								/>
							</Grid>
						))
					: needsAnalysisDropdowns.map((val, index) => {
							return (
								<Grid
									size={6}
									key={`needs-scope-selection-${val.classOfRisk}-${index}`}
								>
									<NeedsAnalysisSelect
										classOfRisk={val.classOfRisk}
										analysisValue={
											needsAnalysis.find((v) => v.key == val.classOfRisk)
												?.value
										}
										disabled={isLoading}
										setAnalysisValue={(v) =>
											handleAnalysisChange(val.classOfRisk, v)
										}
										label={val.label}
									/>
								</Grid>
							);
						})}
		</Grid>
	);
};
